import api from './client';

export interface Notification {
  id: number;
  type: string;
  title: string | null;
  text: string;
  icon: string;
  color: string;
  link: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  /*
   * What the notification is about. The backend sets these alongside `link`
   * (see services/notifier.js) and they are what makes a notification
   * deep-linkable — `link` alone only names a section, e.g. 'listings'.
   */
  referenceType: string | null;
  referenceId: number | null;
}

interface ListResponse {
  status: number;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  unreadCount: number;
  notifications: Notification[];
}

export interface NotificationList {
  total: number;
  unreadCount: number;
  notifications: Notification[];
}

/** GET /api/notifications — the logged-in user's notifications, newest first. */
export const listNotifications = async (
  params: { page?: number; limit?: number; unread?: boolean } = {}
): Promise<NotificationList> => {
  const res = await api.get<ListResponse>('/notifications', {
    params: {
      page: params.page,
      limit: params.limit,
      unread: params.unread ? 'true' : undefined,
    },
  });
  return {
    total: res.data.total,
    unreadCount: res.data.unreadCount,
    notifications: res.data.notifications,
  };
};

/** GET /api/notifications/unread-count — lightweight badge count. */
export const getUnreadNotificationCount = async (): Promise<number> => {
  const res = await api.get<{ status: number; unreadCount: number }>(
    '/notifications/unread-count'
  );
  return res.data.unreadCount;
};

/** PATCH /api/notifications/read-all — mark everything read. */
export const markAllNotificationsRead = async (): Promise<void> => {
  await api.patch('/notifications/read-all');
};

/** PATCH /api/notifications/:id/read — mark a single notification read. */
export const markNotificationRead = async (id: number): Promise<void> => {
  await api.patch(`/notifications/${id}/read`);
};

/** Short "x min ago" style relative time for notification rows. */
export const notificationTimeAgo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day > 1 ? 's' : ''} ago`;
  const mo = Math.floor(day / 30);
  return `${mo} mo ago`;
};
